<template>
  <div class="space-y-5 pb-2">
    <p class="text-sm text-gray-600">
      {{ $t("ticketPurchase.guestDetails.detailsNeeded") }}
    </p>

    <!-- Use same details toggle (only when more than 1 ticket) -->
    <label
      v-if="ticketEntries.length > 1"
      class="flex items-center gap-2 text-sm font-medium text-[#2e234b]"
    >
      <input
        type="checkbox"
        class="accent-pink-500"
        :checked="useSameGuest"
        @change="
          $emit(
            'update:useSameGuest',
            ($event.target as HTMLInputElement).checked,
          )
        "
      />
      {{ $t("ticketPurchase.guestDetails.useSameDetails") }}
    </label>

    <!-- Single form when useSameGuest or only 1 ticket -->
    <template v-if="useSameGuest || ticketEntries.length <= 1">
      <GuestForm
        :guest="ticketEntries[0]?.guest"
        :age-group-options="ageGroupOptions"
        :age-error="ageErrors?.[0]"
        @update:guest="updateGuest(0, $event)"
      />
    </template>

    <!-- Per-ticket forms when not using same guest -->
    <template v-else>
      <div
        v-for="(entry, idx) in ticketEntries"
        :key="`${entry.ticketTypeId}-${entry.ticketIndex}`"
        class="overflow-hidden rounded-xl border"
      >
        <button
          class="flex w-full items-center justify-between bg-gray-50 px-4 py-3 hover:bg-gray-100"
          @click="toggleSection(idx)"
        >
          <span class="text-sm font-semibold text-[#2e234b]">
            {{ entry.ticketTypeName }} #{{ entry.ticketIndex + 1 }}
          </span>
          <svg
            class="h-4 w-4 text-gray-500 transition-transform"
            :class="{ 'rotate-180': openSections[idx] }"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        <div v-if="openSections[idx]" class="p-4">
          <GuestForm
            :guest="entry.guest"
            :age-group-options="ageGroupOptions"
            :age-error="ageErrors?.[idx]"
            @update:guest="updateGuest(idx, $event)"
          />
        </div>
      </div>
    </template>

    <!-- Accept terms (global) -->
    <div class="rounded-xl border border-gray-200 bg-white p-3">
      <label class="flex items-start gap-3 text-sm text-gray-700">
        <input
          type="checkbox"
          class="mt-1 h-4 w-4 accent-pink-500"
          :checked="acceptTerms"
          @change="
            $emit(
              'update:acceptTerms',
              ($event.target as HTMLInputElement).checked,
            )
          "
        />
        <span>
          <i18n-t
            keypath="ticketPurchase.guestDetails.acknowledgement"
            tag="span"
          >
            <template #seller
              ><strong>{{ sellerName }}</strong></template
            >
            <template #termsLink
              ><a
                href="/terms"
                target="_blank"
                class="text-pink-600 underline"
                >{{ $t("ticketPurchase.guestDetails.termsOfService") }}</a
              ></template
            >
          </i18n-t>
        </span>
      </label>
    </div>

    <div class="space-y-2 rounded-xl border border-pink-100 bg-pink-50/60 p-3">
      <label
        class="flex items-start gap-3 rounded-lg border border-pink-100 bg-white/80 p-2.5 text-sm text-gray-700"
      >
        <input
          type="checkbox"
          class="mt-1 h-4 w-4 accent-pink-500"
          :checked="newsletterConsentSeller"
          @change="
            $emit(
              'update:newsletterConsentSeller',
              ($event.target as HTMLInputElement).checked,
            )
          "
        />
        <span class="leading-relaxed">{{
          $t("ticketPurchase.guestDetails.sellerNewsletter", {
            seller: sellerName,
          })
        }}</span>
      </label>

      <label
        class="flex items-start gap-3 rounded-lg border border-pink-100 bg-white/80 p-2.5 text-sm text-gray-700"
      >
        <input
          type="checkbox"
          class="mt-1 h-4 w-4 accent-pink-500"
          :checked="newsletterConsentUpfest"
          @change="
            $emit(
              'update:newsletterConsentUpfest',
              ($event.target as HTMLInputElement).checked,
            )
          "
        />
        <span class="leading-relaxed">{{
          $t("ticketPurchase.guestDetails.upfestNewsletter")
        }}</span>
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import type { GuestInfo, TicketEntry } from "./types";
import GuestForm from "./GuestForm.vue";

const props = defineProps<{
  ticketEntries: TicketEntry[];
  useSameGuest: boolean;
  ageGroupOptions: string[];
  acceptTerms: boolean;
  newsletterConsentSeller: boolean;
  newsletterConsentUpfest: boolean;
  sellerName: string;
  ageErrors?: Record<number, string>;
}>();

const emit = defineEmits<{
  (e: "update:ticketEntries", value: TicketEntry[]): void;
  (
    e:
      | "update:useSameGuest"
      | "update:acceptTerms"
      | "update:newsletterConsentSeller"
      | "update:newsletterConsentUpfest",
    value: boolean,
  ): void;
}>();

// Collapsible sections for per-ticket mode
const openSections = ref<Record<number, boolean>>({});
function toggleSection(idx: number) {
  openSections.value[idx] = !(openSections.value[idx] ?? true);
}
watch(
  () => props.ticketEntries.length,
  () => {
    for (let i = 0; i < props.ticketEntries.length; i++) {
      if (openSections.value[i] === undefined) openSections.value[i] = true;
    }
  },
  { immediate: true },
);

function updateGuest(idx: number, guest: GuestInfo) {
  const updated = [...props.ticketEntries];
  const existing = updated[idx];
  if (!existing) return;
  updated[idx] = { ...existing, guest };
  emit("update:ticketEntries", updated);
}
</script>
